import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function titleCase(str: string): string {
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function upperCase(str: string): string {
  return str.toUpperCase();
}

export function toLocalYMD(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
}

export function formatDateOnly(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateTime(dateStr: string): { date: string; time: string } {
  if (!dateStr) return { date: '', time: '' };
  const date = new Date(dateStr);
  return {
    date: date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  };
}

export function formatMapTimestamp(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  // Format: Wed, Dec 03, 2025 11:30 AM
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

export const formatLastLogin = (lastLogin: string | null) => {
  if (!lastLogin) return "Never";
  return new Date(lastLogin).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/*** Formats YYYY-MM-DD date strings into human-friendly format: e.g. "1st April 2026"
 * Non-date labels (e.g. lanes, statuses) are returned unchanged.*/
export function formatChartDate(label: string): string {
  if (!label) return '';
  const match = label.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = match[1];
    const monthIndex = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const monthName = monthNames[monthIndex];
    if (monthName && !isNaN(day)) {
      return `${getOrdinalSuffix(day)} ${monthName} ${year}`;
    }
  }
  return label;
}

/*** Returns a YYYY-MM-DD string for a given Date in IST (Asia/Kolkata).
 * Uses en-CA locale which reliably produces YYYY-MM-DD format.*/
export function toISTDateString(date: Date): string {
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

/*** Parses UTC timestamps without a timezone suffix (e.g. "2026-10-12T05:25:05")
 * as UTC before converting to local display format.*/
export function parseUtcDate(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  let iso = dateStr.trim();
  if (!iso.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(iso)) {
    iso = `${iso}Z`;
  }
  const date = new Date(iso);
  return isNaN(date.getTime()) ? null : date;
}

export function formatUtcToLocal(dateStr?: string | null): string {
  const date = parseUtcDate(dateStr);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatUtcToLocalDateOnly(dateStr?: string | null): string {
  const date = parseUtcDate(dateStr);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}